import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles, RotateCcw } from "lucide-react";
import { http, gradeLabel } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { StepRail } from "@/components/lesson/StepRail";
import { CenteringStep } from "@/components/lesson/CenteringStep";
import { LessonStep } from "@/components/lesson/LessonStep";
import { QuizStep } from "@/components/lesson/QuizStep";
import { ReflectStep } from "@/components/lesson/ReflectStep";

const INITIAL = { intention: "", reflection: "", gratitude: "", mood_before: 3, mood_after: 3, quiz_id: null, mindful_minutes: 0.5 };

const Complete = ({ subjectId }) => (
  <div className="text-center py-16 rise" data-testid="lesson-complete">
    <Sparkles className="w-10 h-10 mx-auto text-[#D99B26]" />
    <h2 className="font-display text-4xl text-[#2C4A3E] mt-4">Lesson complete</h2>
    <p className="text-stone-600 mt-3">You centered yourself, learned, practiced and reflected. That's conscious learning.</p>
    <div className="flex justify-center gap-3 mt-8">
      <Link to="/journal" data-testid="complete-view-journal-btn" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-white">View journal</Link>
      <Link to={`/subject/${subjectId}`} data-testid="complete-next-topic-btn" className="px-6 py-3 rounded-full border border-[#E6DEC3]">Another topic</Link>
    </div>
  </div>
);

const STEP_NAMES = ["Center", "Learn", "Practice", "Reflect"];
const saveKey = (sid, topic, assignedId) => `${sid}:${assignedId ? `assigned:${assignedId}` : topic}`;

const ResumeBar = ({ step, onRestart }) => (
  <div className="mt-6 rounded-2xl bg-[#FBF3E6] border border-[#C87D53]/30 px-5 py-3 flex flex-wrap items-center gap-3 text-sm rise" data-testid="lesson-resume-bar">
    <span className="flex-1 min-w-[200px] text-[#2C4A3E]">Welcome back! You're picking up where you left off, at <b>{STEP_NAMES[step]}</b>.</span>
    <button onClick={onRestart} data-testid="lesson-start-over-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E6DEC3] bg-white hover:bg-[#F1EADB]"><RotateCcw className="w-4 h-4" /> Start over</button>
  </div>
);

export default function LessonFlow() {
  const { subjectId } = useParams();
  const [params] = useSearchParams();
  const assignedId = params.get("assigned");
  const [assigned, setAssigned] = useState(null);
  const [assignedError, setAssignedError] = useState(false);
  const { profile, setTutorContext } = useProfile();
  const [subject, setSubject] = useState(null);
  const [step, setStep] = useState(0);
  const [lesson, setLesson] = useState("");
  const [data, setData] = useState(INITIAL);
  const [selfPrompt, setSelfPrompt] = useState("");
  const [profileGrade] = useState(profile.grade);
  const topic = assigned?.topic || params.get("topic") || "";
  const grade = assigned?.grade || profileGrade;
  const focus = params.get("focus") || undefined;
  const forAssignment = params.get("for");
  const req = useMemo(() => ({ subject_id: subjectId, grade, topic, assigned_lesson_id: assignedId, focus }), [subjectId, grade, topic, assignedId, focus]);
  const key = saveKey(subjectId, `${params.get("topic") || ""}${focus ? `|${focus}` : ""}`, assignedId);
  const [resumed, setResumed] = useState(false);
  const [restoredFor, setRestoredFor] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    http.get("/lesson-progress", { params: { key } }).then((r) => {
      const s = r.data.state;
      if (!alive) return;
      if (s && s.step > 0 && s.step < 4) {
        setStep(s.step); setData({ ...INITIAL, ...s.data }); setSelfPrompt(s.selfPrompt || "");
        if (s.lesson && !assignedId) setLesson(s.lesson);
        setResumed(true);
      }
    }).catch(() => {}).finally(() => alive && setRestoredFor(key));
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (restoredFor !== key) return;
    if (step >= 4) { http.delete("/lesson-progress", { params: { key } }).catch(() => {}); return; }
    if (step === 0) return;
    const t = setTimeout(() => http.put("/lesson-progress", { key, state: { step, data, selfPrompt, lesson: assignedId || (step === 1 && !/## Discovering Myself[\s\S]*\?/.test(lesson)) ? "" : lesson } }).catch(() => {}), 800);
    return () => clearTimeout(t);
  }, [key, restoredFor, step, data, selfPrompt, lesson, assignedId]);
  const restart = () => {
    http.delete("/lesson-progress", { params: { key } }).catch(() => {}); setResumed(false); setStep(0); setData(INITIAL); setSelfPrompt("");
    setLesson(assigned ? assigned.content : ""); setNonce((n) => n + 1);
  };

  useEffect(() => { http.get("/subjects").then((r) => setSubject(r.data.find((s) => s.id === subjectId))); }, [subjectId]);
  useEffect(() => {
    if (assignedId) http.get(`/assigned/${assignedId}`).then((r) => { setAssigned(r.data); setLesson(r.data.content); }).catch(() => setAssignedError(true));
  }, [assignedId]);
  useEffect(() => { setTutorContext({ subject_id: subjectId, topic }); return () => setTutorContext({}); }, [subjectId, topic, setTutorContext]);
  if (assignedError) return (
    <div className="max-w-6xl mx-auto px-6 py-20 text-center" data-testid="assigned-error">
      <p className="font-display text-2xl text-[#2C4A3E]">This assigned lesson isn't available anymore.</p>
      <Link to="/" className="inline-block mt-5 px-6 py-3 rounded-full bg-[#2C4A3E] text-white" data-testid="assigned-error-home-btn">Back to home</Link>
    </div>
  );
  if (restoredFor !== key) return null;
  if (assignedId && !assigned) return <div className="max-w-6xl mx-auto px-6 py-20 text-stone-500" data-testid="assigned-loading">Loading your assigned lesson…</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="lesson-flow-page" data-translate>
      <Link to={`/subject/${subjectId}`} className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#2C4A3E]" data-testid="lesson-back-link">
        <ArrowLeft className="w-4 h-4" /> {subject?.name || "Back"}
      </Link>
      <div className="mt-6 flex flex-wrap justify-between gap-6 items-end">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#C87D53] font-semibold">{subject?.short} · {gradeLabel(grade)}{assigned && <span data-testid="lesson-assigned-badge" className="ml-2 px-2 py-0.5 rounded-full bg-[#E3EBE5] text-[#2C4A3E] normal-case tracking-normal">Assigned by your teacher</span>}</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#2C4A3E] mt-2" data-testid="lesson-topic-title">{topic}</h1>
          {profile?.lesson_length && profile.lesson_length !== "standard" && <span className="ml-1 mt-2 inline-block px-3 py-1 rounded-full bg-[#F1EADB] text-xs text-[#2C4A3E] capitalize" data-testid="lesson-length-chip">{profile.lesson_length} lesson</span>}
          {forAssignment && <p className="mt-2 inline-block px-3 py-1.5 rounded-full bg-[#FBF3E2] border border-[#F2D49B] text-sm text-[#7A4A12]" data-testid="lesson-help-banner">Helping you understand: <b>{forAssignment}</b>{focus ? ` · ${focus}` : ""}</p>}
        </div>
        {step < 4 && <StepRail current={step} />}
      </div>
      {resumed && step > 0 && step < 4 && <ResumeBar step={step} onRestart={restart} />}
      <div className="mt-12" key={nonce}>
        {step === 0 && <CenteringStep topic={topic} data={data} setData={setData} onDone={() => setStep(1)} />}
        {step === 1 && <LessonStep req={req} lesson={lesson} setLesson={setLesson} locked={!!assigned} onDone={() => setStep(2)} />}
        {step === 2 && <QuizStep req={req} lesson={lesson} onDone={(quizId, p) => { setData({ ...data, quiz_id: quizId }); setSelfPrompt(p); setStep(3); }} />}
        {step === 3 && <ReflectStep req={req} lesson={lesson} data={data} setData={setData} selfPrompt={selfPrompt} onSaved={() => setStep(4)} />}
        {step === 4 && <Complete subjectId={subjectId} />}
      </div>
    </div>
  );
}
